import { usePresentationStore } from '@components/Presentation/store/presentationStore';
import { usePresentationUIStore } from '@components/Presentation/store/presentationUIStore';
import { useSelectedSlide } from '@components/Presentation/hooks/useSelectedSlide';
import React, { useRef, useState, useEffect } from 'react';
import SettingBlock from './units/SettingBlock';
import OneAnswerPerPerson from './units/OneAnswerPerPerson';
import ShowCommentsOverlay from './units/ShowCommentsOverlay';
import RoSColorPicker from './units/RoSColorPicker';
import ApplyAllOrReset from './units/ApplyAllOrReset';
import MapSound from './units/MapSound';
import Input from '@atoms/Input';
import { panelBtnEnum } from '@components/Presentation/constants';
import { calculateRemainingChars } from '@components/Presentation/helpers';
import MaxLengthCounter from '@components/Presentation/atoms/MaxLengthCounter';
import useFocusRef from '@components/Presentation/hooks/useFocusRef';
import SlideHeading from './units/SlideHeading';
import BackgroundImage from './units/BackgroundImage';
import { computeLuminance } from '@lib/interactionHelper';
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger
} from '@atoms/new/dropdown';
import Button from '@atoms/new/button';
import useSound from 'use-sound';
import { soundUrls, mapSoundOptions } from '@services/interactions-service';
import {
  CaretDownIcon,
  CheckIcon as DoneRounded,
  InfoIcon as InfoOutlined,
  PlayIcon as PlayArrowRoundedIcon
} from '@phosphor-icons/react';

const mapContinents = [
  { name: 'World', value: 'world' },
  { name: 'Asia', value: 'asia' },
  { name: 'Africa', value: 'africa' },
  { name: 'Australia', value: 'australia' },
  { name: 'Europe', value: 'europe' },
  { name: 'North America', value: 'north-america' },
  { name: 'South America', value: 'south-america' }
];

export type MapStyle =
  | 'Standard'
  | 'Globe'
  | 'Comic'
  | 'Pencil'
  | 'Blueprint'
  | 'Bright'
  | 'Bright (Globe)'
  | 'Light'
  | 'Dark'
  | 'Sky';

export const mapStyles: {
  name: string;
  value: MapStyle;
  url: string;
  styleColors: {
    waterColor: string;
    landColor: string;
  };
}[] = [
  {
    name: 'Standard',
    value: 'Standard',
    url: '/images/mapstyles/default.png',
    styleColors: {
      waterColor: '#5CC0EB',
      landColor: '#BFDBAC'
    }
  },
  {
    name: 'Light',
    value: 'Light',
    url: '/images/mapstyles/light.png',
    styleColors: {
      waterColor: '#DBDBDC',
      landColor: '#FCFCFD'
    }
  },
  {
    name: 'Dark',
    value: 'Dark',
    url: '/images/mapstyles/dark.png',
    styleColors: {
      waterColor: '#1A1A1A',
      landColor: '#292929'
    }
  },
  {
    name: 'Sky',
    value: 'Sky',
    url: '/images/mapstyles/sky.png',
    styleColors: {
      waterColor: '#7EDCF6',
      landColor: '#CDF3FE'
    }
  },
  {
    name: 'Globe',
    value: 'Globe',
    url: '/images/mapstyles/globe.png',
    styleColors: {
      waterColor: '#52BDEA',
      landColor: '#C6DCB5'
    }
  },
  {
    name: 'Comic',
    value: 'Comic',
    url: '/images/mapstyles/comic.png',
    styleColors: {
      waterColor: '#CDD4DD',
      landColor: '#FFFFFF'
    }
  },
  {
    name: 'Pencil',
    value: 'Pencil',
    url: '/images/mapstyles/pencil.png',
    styleColors: {
      waterColor: '#B6B5B5',
      landColor: '#B9B9B9'
    }
  },
  {
    name: 'Blueprint',
    value: 'Blueprint',
    url: '/images/mapstyles/blueprint.png',
    styleColors: {
      waterColor: '#415EAF',
      landColor: '#415EAF'
    }
  },
  {
    name: 'Minimal',
    value: 'Bright',
    url: '/images/mapstyles/default.png',
    styleColors: {
      waterColor: '#5CC0EB',
      landColor: '#BFDBAC'
    }
  },
  {
    name: 'Minimal (Globe)',
    value: 'Bright (Globe)',
    url: '/images/mapstyles/globe.png',
    styleColors: {
      waterColor: '#52BDEA',
      landColor: '#C6DCB5'
    }
  }
];

function MagicMapSettings() {
  const selectedSlide = useSelectedSlide();
  const selectedSlideIndex = usePresentationStore(s => s.selectedSlideIndex);
  const selectedPanelBtn = usePresentationStore(s => s.selectedPanelBtn);
  const disableAutoFocus = usePresentationUIStore(s => s.disableAutoFocus);
  const [isTitleFocused, setIsTitleFocused] = useState(false);
  const [showApplyColor, setShowApplyColor] = useState(false);
  const inputRef = useFocusRef<HTMLInputElement>([selectedSlide.type], disableAutoFocus);
  const mapConfig = selectedSlide.setting.mapConfig ?? { style: 'Standard', terrain: false };
  const isConfigurableStyle = !['Light', 'Dark', 'Sky', 'Pencil', 'Blueprint',].includes(mapConfig?.style);

  const styleColorMap: Record<string, { mapWaterColor: string; mapLandColor: string }> =
    mapStyles.reduce<Record<string, { mapWaterColor: string; mapLandColor: string }>>(
      (acc, style) => {
        acc[style.value] = isConfigurableStyle
          ? {
            mapWaterColor: selectedSlide.color.mapWaterColor,
            mapLandColor: selectedSlide.color.mapLandColor
          }
          : {
            mapWaterColor: style.styleColors.waterColor,
            mapLandColor: style.styleColors.landColor
          };

        return acc;
      },
      {}
    );

  const previousColorsRef = useRef(styleColorMap);

  const getOptionName = () => {
    const option = mapContinents.find(
      (option) => option.value === selectedSlide.setting.DefaultView
    );
    return option?.name || 'World';
  };

  const getSelectedStyleName = (style?: string) => {
    if (!style) return 'Standard';
    if (style === 'Bright') return 'Minimal';
    if (style === 'Bright (Globe)') return 'Minimal (Globe)';
    return style;
  };

  const [play, { stop }] = useSound(soundUrls[selectedSlide.setting.mapSoundOption ?? 'pop'], {
    playbackRate: 0.75,
    volume: 0.5,
  });

  useEffect(() => {
    setShowApplyColor(false);
    previousColorsRef.current = styleColorMap;
  }, [selectedSlideIndex]);

  return (
    <>
      {selectedPanelBtn === panelBtnEnum.content && (
        <>
          <SettingBlock
            title="Heading" defaultExpanded={true}
            tooltip="Type your question here."
            titleMargin="sm"
            hasBorderTop={false}
          >
            <div>
              <Input
                ref={inputRef}
                placeholder="Type a prompt or question..."
                value={selectedSlide.title}
                onChange={selectedSlide.handler.updateTitle}
                size="lg"
                maxLength={255}
                handleFocus={(v) => {
                  setIsTitleFocused(v);
                  if (v) {
                    inputRef.current?.select();
                  }
                }}
                addOnRight={
                  isTitleFocused ? (
                    <MaxLengthCounter
                      value={calculateRemainingChars(selectedSlide.title)}
                    />
                  ) : null
                }
              />
              {calculateRemainingChars(selectedSlide.title) === 0 && (
                <p className="text-sm text-alert mt-1">
                  You have reached the maximum length
                </p>
              )}
            </div>
          </SettingBlock>
          <SettingBlock
            title="Map view"
            tooltip="Initial map view to be shown. You can still pan and zoom over the entire map during a presentation."
            titleMargin="sm"
            hasBorderTop
          >
            <Dropdown className="z-1 w-[300px]">
              <DropdownTrigger>
                <Button
                  kind="tertiary"
                  fullWidth
                  className=" flex items-center gap-3"
                >
                  <p className='font-normal'>{getOptionName()}</p>
                  <div className="ml-auto">
                    <CaretDownIcon size={16} />
                  </div>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {mapContinents.map((option) => (
                  <DropdownItem
                    key={option.value}
                    value={option.value}
                    onAction={() =>
                      selectedSlide.handler.updateSetting(
                        'DefaultView',
                        option.value
                      )
                    }
                    endContent={
                      option.value === selectedSlide.setting.DefaultView ? (
                        <DoneRounded size={20} />
                      ) : null
                    }
                  >
                    {option.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </SettingBlock>

          <SettingBlock title="Audience responses">
            <OneAnswerPerPerson />
            <ShowCommentsOverlay />
          </SettingBlock>
          <SettingBlock title="Sound">
            <MapSound />
            <div className='flex flex-col gap-2'>
              <div className="self-stretch inline-flex justify-start items-center gap-2.5">
                <div className={`${!selectedSlide.setting.mapSound && 'opacity-50'}  justify-start text-default-900 text-sm font-normal leading-tight`}>Location:</div>
              </div>
              <div className='flex items-center justify-center gap-2'>
                <Dropdown className="z-1" isDisabled={!selectedSlide.setting.mapSound}>
                  <DropdownTrigger>
                    <Button
                      kind="tertiary"
                      fullWidth
                      className="flex items-center gap-3"
                    >
                      <p className='font-normal'>
                        {mapSoundOptions.find(
                          (option) =>
                            option.value === selectedSlide.setting.mapSoundOption
                        )?.name || 'Pop'}
                      </p>
                      <div className="ml-auto">
                        <CaretDownIcon size={16} />
                      </div>
                    </Button>
                  </DropdownTrigger>
                  <DropdownMenu className="w-[260px]">
                    {mapSoundOptions.map((option) => (
                      <DropdownItem
                        key={option.value}
                        value={option.value}
                        onAction={() =>
                          selectedSlide.handler.updateSetting(
                            'mapSoundOption',
                            option.value
                          )
                        }
                        endContent={
                          option.value === selectedSlide.setting.mapSoundOption ? (
                            <DoneRounded size={20} />
                          ) : null
                        }
                      >
                        {option.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
                <Button
                  kind='secondary'
                  isIconOnly={true}
                  isDisabled={!selectedSlide.setting.mapSound}
                  onClick={() => {
                    if (!selectedSlide.setting.mapSound) return;
                    play();
                  }}
                  size='md'>
                  <PlayArrowRoundedIcon size={20} className={`${selectedSlide.setting.mapSound ? 'text-grey-900' : 'text-grey-400'}`} />
                </Button>
              </div>
            </div>
          </SettingBlock>
        </>
      )}
      {selectedPanelBtn === panelBtnEnum.design && (
        <>
          <SettingBlock
            title="Map style"
            defaultExpanded={true}
            hasBorderDown
            hasBorderTop={false}
          >
            {isConfigurableStyle &&
              showApplyColor && (
              <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 p-3 rounded-md dark:text-grey-900">
                <InfoOutlined size={20}
                />
                <p className="text-sm">
                    Want to use previous styles? Click Apply.
                </p>
                <Button
                  kind="secondary"
                  onPress={() => {
                    selectedSlide.handler.updateColor(
                      'mapWaterColor',
                      previousColorsRef.current[mapConfig?.style]?.mapWaterColor
                    );
                    selectedSlide.handler.updateColor(
                      'mapLandColor',
                      previousColorsRef.current[mapConfig?.style]?.mapLandColor
                    );
                    setShowApplyColor(false);
                  }}
                >
                    Apply
                </Button>
              </div>
            )}
            <Dropdown
              className="z-1 w-[300px]"
            >
              <DropdownTrigger>
                <Button
                  kind="tertiary"
                  fullWidth
                  className=" flex items-center gap-3 font-normal"
                  startContent={
                    <img
                      src={
                        mapStyles.find(
                          (option) =>
                            option.value === (mapConfig?.style || 'Standard')
                        )?.url
                      }
                      alt="map style"
                      className="size-6 object-cover rounded-md"
                    />
                  }
                >
                  {getSelectedStyleName(mapConfig?.style)}
                  <div className="ml-auto">
                    <CaretDownIcon size={16} />
                  </div>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                {mapStyles.map((option) => (
                  <DropdownItem
                    key={option.value}
                    value={option.value}
                    onAction={() => {
                      setShowApplyColor(false);

                      selectedSlide.handler.updateSetting('mapConfig', {
                        ...mapConfig,
                        style: option.value
                      });

                      const landColor = option.styleColors.landColor;
                      const waterColor = option.styleColors.waterColor;

                      selectedSlide.handler.updateColor(
                        'mapWaterColor',
                        waterColor
                      );

                      selectedSlide.handler.updateColor(
                        'mapLandColor',
                        landColor
                      );

                      const isDefaultColor = option.styleColors.waterColor === previousColorsRef.current[option.value]?.mapWaterColor
                        && option.styleColors.landColor === previousColorsRef.current[option.value]?.mapLandColor;

                      if (!isDefaultColor) setShowApplyColor(true);
                    }}
                    startContent={
                      <img
                        src={option.url}
                        alt={option.name}
                        className="size-5 object-cover rounded-md"
                      />
                    }
                    endContent={
                      (!mapConfig && option.value === 'Standard') ||
                        option.value === mapConfig?.style ? (
                          <DoneRounded size={20} />
                        ) : null
                    }
                  >
                    {option.name}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
          </SettingBlock>
          <SettingBlock
            title="Visualization colors"
            hasBorderTop={false}
            addPadding
          >
            <RoSColorPicker
              disableGradient
              disableOpacity
              showPopoverContent={isConfigurableStyle}
              color={selectedSlide.color.mapWaterColor}
              onChange={(color) => {
                selectedSlide.handler.updateColor('mapWaterColor', color);
                Object.keys(previousColorsRef.current).forEach((style) => {
                  if (!isConfigurableStyle) return;
                  previousColorsRef.current[style] = {
                    ...previousColorsRef.current[style],
                    mapWaterColor: color
                  };
                });
              }}
            >
              Water
            </RoSColorPicker>
            <RoSColorPicker
              disableGradient
              disableOpacity
              showPopoverContent={isConfigurableStyle}
              color={selectedSlide.color.mapLandColor}
              onChange={(color) => {
                selectedSlide.handler.updateColor('mapLandColor', color);
                Object.keys(previousColorsRef.current).forEach((style) => {
                  if (!isConfigurableStyle) return;
                  previousColorsRef.current[style] = {
                    ...previousColorsRef.current[style],
                    mapLandColor: color
                  };
                });
              }}
            >
              Land
            </RoSColorPicker>
            <RoSColorPicker
              disableGradient
              color={selectedSlide.color.latestPointColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('latestPointColor', color)
              }
            >
              Latest location
            </RoSColorPicker>
            <RoSColorPicker
              disableGradient
              color={selectedSlide.color.individualPointColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('individualPointColor', color)
              }
            >
              Locations
            </RoSColorPicker>
            {/* <RoSColorPicker
              disableGradient
              color={selectedSlide.color.smallCircleColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('smallCircleColor', color)
              }
            >
              Cluster
            </RoSColorPicker> */}
            {/* <RoSColorPicker
              disableGradient
              showPopoverContent={isConfigurableStyle}
              color={selectedSlide.color.midCircleColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('midCircleColor', color)
              }
            >
              Mid cluster
            </RoSColorPicker>
            <RoSColorPicker
              disableGradient
              showPopoverContent={isConfigurableStyle}
              color={selectedSlide.color.bigCircleColor}
              onChange={(color) =>
                selectedSlide.handler.updateColor('bigCircleColor', color)
              }
            >
              Big cluster
            </RoSColorPicker> */}
          </SettingBlock>
          <SettingBlock
            title="Background"
            hasBorderTop
          >
            <RoSColorPicker
              color={selectedSlide.color.mapBgColor || '#000000'}
              onChange={(color) => {
                selectedSlide.handler.updateColor('mapBgColor', color);
                const { isBgLight } = computeLuminance(color);
                const newEvent = isBgLight ? '#111111' : '#ffffff';
                selectedSlide.handler.updateColor('mapHeadingColor', newEvent);
              }}
            >
              Background color
            </RoSColorPicker>
            <BackgroundImage />
          </SettingBlock>
          <SlideHeading
            color={selectedSlide.color.mapHeadingColor}
            updateColor={(color) =>
              selectedSlide.handler.updateColor('mapHeadingColor', color)
            }
          />
          <ApplyAllOrReset />
        </>
      )}
    </>
  );
}

export default MagicMapSettings;
